import { Link, useParams } from 'react-router-dom'
import { PortableText, type PortableTextBlock } from '@portabletext/react'
import { Breadcrumb } from '@autoplan/web-ui'
import { useNewsArticle, useLocale, urlFor } from '@autoplan/web-sanity'
import { toLocaleCode } from '@autoplan/common'
import type { SanityImageSource } from '@sanity/image-url'

export const Article = () => {
  const { slug } = useParams<{ slug: string }>()
  const { article, articleImageUrl } = useNewsArticle(slug)
  const { lang, t } = useLocale()

  if (!article) return null

  return (
    <article>
      <div className="ap:w-[85%] ap:mx-auto">
        <div className="ap:mb-2xl">
          <Breadcrumb
            items={[
              { path: '/', label: t('home.breadcrumb.home') },
              { path: '/articles', label: t('home.breadcrumb.news') },
              { path: `/articles/${slug}`, label: article.title },
            ]}
            linkAs={Link}
          />
        </div>

        <h1 className="ap:text-3xl ap:font-bold ap:leading-tight ap:mb-lg">{article.title}</h1>

        {articleImageUrl && (
          <div className="ap:aspect-video ap:overflow-hidden ap:rounded-xl ap:mb-xl">
            <img src={articleImageUrl} alt={article.title} className="ap:w-full ap:h-full ap:object-cover" />
          </div>
        )}

        {article.publishedAt && (
          <p className="ap:text-sm ap:text-content-disabled ap:mb-2xl">
            {new Date(article.publishedAt).toLocaleDateString(toLocaleCode(lang), {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        )}

        {Array.isArray(article.body) && (
          <PortableText value={article.body as PortableTextBlock[]} components={portableTextComponents} />
        )}
      </div>
    </article>
  )
}

const portableTextComponents = {
  types: {
    image: ({ value }: { value: SanityImageSource & { alt?: string } }) => {
      const url = urlFor(value)?.width(800).url()
      if (!url) return null
      return (
        <figure className="ap:my-3xl">
          <img src={url} alt={value.alt ?? ''} className="ap:w-1/2 ap:rounded-xl" />
        </figure>
      )
    },
  },
  block: {
    normal: ({ children }: { children?: React.ReactNode }) => (
      <p className="ap:text-base ap:leading-relaxed ap:text-content-primary ap:mb-xl">{children}</p>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
      <h2 className="ap:text-2xl ap:font-bold ap:mt-3xl ap:mb-lg">{children}</h2>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
      <h3 className="ap:text-xl ap:font-semibold ap:mt-2xl ap:mb-md">{children}</h3>
    ),
    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <blockquote className="ap:border-l-4 ap:border-stroke-primary ap:pl-xl ap:italic ap:text-content-secondary ap:my-2xl">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }: { children?: React.ReactNode }) => (
      <ul className="ap:list-disc ap:list-inside ap:mb-xl ap:space-y-xs">{children}</ul>
    ),
    number: ({ children }: { children?: React.ReactNode }) => (
      <ol className="ap:list-decimal ap:list-inside ap:mb-xl ap:space-y-xs">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }: { children?: React.ReactNode }) => (
      <li className="ap:text-base ap:leading-relaxed ap:text-content-primary">{children}</li>
    ),
    number: ({ children }: { children?: React.ReactNode }) => (
      <li className="ap:text-base ap:leading-relaxed ap:text-content-primary">{children}</li>
    ),
  },
}
